/**
 * Orders search + status filter + date range + view switcher.
 *
 * The BRANCH filter is the app-bar picker (StorePicker), not a control here.
 *
 * Everything filters SERVER-side through react-admin's list filters, not over the
 * loaded page — filtering client-side would only ever search the rows currently
 * in memory and silently miss the rest of the history.
 *
 * Search spans order number, customer name, email and cell number.
 * Refunded is not an order status (it lives on refundStatus), so it goes out as
 * its own `refunded` flag rather than as `status`.
 */

import React, { useEffect, useState } from 'react';
import {
  Box, InputBase, IconButton, Menu, MenuItem, Tooltip, Divider,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { useListContext } from 'react-admin';
import { useHUDTheme } from '../theme';
import { monoLabelSx } from '../../reskin/primitives';
import { useFilterPatch } from '../useFilterPatch';
import { STATUS_FILTER_OPTIONS, type StatusKey } from '../statusMeta';

const SEARCH_DEBOUNCE_MS = 350;

/**
 * The API's `to` is an exclusive upper bound on createdAt (the endDate scope
 * uses `Op.lt`), so a plain end date would silently exclude that whole day.
 * Send the following midnight instead — "26th to 26th" then means the 26th.
 */
function exclusiveEnd(day: string): string {
  const d = new Date(`${day}T00:00:00`);
  d.setDate(d.getDate() + 1);
  const pad = (n: number) => String(n).padStart(2, '0');
  return sastInstant(`${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`);
}

/**
 * The API declares from/to as `format: date-time`, so a bare YYYY-MM-DD is
 * rejected with a 400 and the Orders list empties. It also compares against
 * createdAt (timestamptz) on a UTC connection, so the boundary must be pinned to
 * SAST or "the 26th" would start at 02:00 and swallow two hours of the 27th.
 */
function sastInstant(day: string): string {
  return `${day}T00:00:00+02:00`;
}

/** Strip an instant back to its SAST calendar day for the date input. */
function toDayInput(value: string): string {
  return (value || '').slice(0, 10);
}

/** Inverse of exclusiveEnd, for showing the stored filter back in the input. */
function inclusiveEnd(value: string): string {
  const day = toDayInput(value);
  const d = new Date(`${day}T00:00:00`);
  d.setDate(d.getDate() - 1);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/**
 * Open the native calendar from anywhere on the field.
 *
 * A bare <input type="date"> only opens its picker from the little calendar
 * glyph — clicking the yyyy/mm/dd text just puts a caret in a segment, so the
 * control looked broken unless you hit a ~16px target. showPicker() is the
 * supported way to trigger it; it throws if the browser refuses (no user
 * gesture, or already open), which is nothing to act on.
 */
function openDatePicker(e: React.MouseEvent<HTMLInputElement> | React.FocusEvent<HTMLInputElement>): void {
  const el = e.currentTarget;
  if (typeof el.showPicker !== 'function') return;
  try {
    el.showPicker();
  } catch {
    /* picker already open, or the browser declined — the field still works */
  }
}

/** Native date input, dressed to match the surrounding HUD controls. */
const dateInputSx = (hud: ReturnType<typeof useHUDTheme>['hud'], mode: 'light' | 'dark') => ({
  bgcolor: 'transparent',
  border: 'none',
  outline: 'none',
  color: hud.textPrimary,
  fontSize: '0.8125rem',
  fontWeight: 600,
  fontFamily: 'inherit',
  // Native picker glyphs follow the ground, or they vanish on the light grounds.
  colorScheme: mode,
  cursor: 'pointer',
  p: 0,
});

/** The prototype's `.fsel` / `.fsearch` shell: 32px, raised, hairline, 6px radius. */
const pillSx = (hud: ReturnType<typeof useHUDTheme>['hud'], active = false) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 1,
  height: 32,
  px: 1.4,
  bgcolor: hud.surface,
  border: `1px solid ${active ? hud.accent : hud.border}`,
  borderRadius: 'var(--r-ctl, 6px)',
  fontSize: '0.8125rem',
  fontWeight: 600,
  color: hud.textPrimary,
  whiteSpace: 'nowrap' as const,
});

/** The small tracked key inside a pill — "STATUS", "FROM". */
const pillKeySx = (hud: ReturnType<typeof useHUDTheme>['hud']) => ({
  ...monoLabelSx,
  fontWeight: 500,
  fontSize: '0.66rem',
  letterSpacing: '0.1em',
  color: hud.textMuted,
});

export default function OrdersFilterBar() {
  const { hud, mode } = useHUDTheme();
  const { filterValues } = useListContext();
  const patchFilters = useFilterPatch();

  const activeStatus: StatusKey | null =
    filterValues?.refunded ? 'refunded' : (filterValues?.status ?? null);

  const [term, setTerm] = useState<string>(filterValues?.search ?? '');
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  const from: string = toDayInput(filterValues?.from ?? '');
  const to: string = filterValues?.to ? inclusiveEnd(filterValues.to) : '';
  const hasRange = !!(from || to);

  // Debounced so we don't fire a request per keystroke.
  useEffect(() => {
    const id = setTimeout(() => {
      const wanted = term.trim();
      // Patching is a no-op when nothing changed, so mount doesn't refetch.
      patchFilters({ search: wanted || undefined });
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(id);
  }, [term]); // eslint-disable-line react-hooks/exhaustive-deps

  /** null = All statuses, i.e. no status/refunded constraint at all. */
  const applyStatus = (key: StatusKey | null) => {
    patchFilters({
      status: key && key !== 'refunded' ? key : undefined,
      refunded: key === 'refunded' ? true : undefined,
    });
    setAnchor(null);
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '9px', mb: '13px', flexWrap: 'wrap' }}>
      {/* Search */}
      <Box sx={{ ...pillSx(hud), flex: 1, minWidth: 240, fontWeight: 400 }}>
        <SearchIcon sx={{ fontSize: 15, color: hud.textMuted }} />
        <InputBase
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          placeholder="Search order ID, name, email or cell number"
          sx={{ flex: 1, color: hud.textPrimary, fontSize: '0.8125rem', '& input::placeholder': { color: hud.textMuted, opacity: 1 } }}
        />
        {term && (
          <IconButton size="small" onClick={() => setTerm('')} sx={{ color: hud.textMuted }}>
            <ClearIcon sx={{ fontSize: 16 }} />
          </IconButton>
        )}
      </Box>

      {/* Status filter */}
      <Box
        role="button"
        tabIndex={0}
        onClick={(e) => setAnchor(e.currentTarget as HTMLElement)}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setAnchor(e.currentTarget as HTMLElement); }}
        sx={{ ...pillSx(hud, !!activeStatus), cursor: 'pointer', '&:hover': { bgcolor: hud.hover } }}
      >
        <Box component="b" sx={pillKeySx(hud)}>Status</Box>
        {activeStatus ? STATUS_FILTER_OPTIONS.find((o) => o.key === activeStatus)?.label ?? activeStatus : 'All'}
        <Box component="span" sx={{ fontSize: '0.55rem', color: hud.textMuted }}>▼</Box>
      </Box>

      <Menu anchorEl={anchor} open={!!anchor} onClose={() => setAnchor(null)}>
        {/* Clears status AND refunded — "All statuses" really does mean all. */}
        <MenuItem selected={!activeStatus} onClick={() => applyStatus(null)} sx={{ fontWeight: 700 }}>
          All statuses
        </MenuItem>
        <Divider />
        {STATUS_FILTER_OPTIONS.map(({ key, label, color }) => (
          <MenuItem key={key} selected={activeStatus === key} onClick={() => applyStatus(key)}>
            <Box sx={{ width: 9, height: 9, borderRadius: '50%', bgcolor: color, mr: 1.25, flexShrink: 0 }} />
            {label}
          </MenuItem>
        ))}
      </Menu>

      {/* Date range. The API has taken from/to all along; nothing exposed it, so
          "show me Saturday" was impossible on a page that is order history. */}
      <Box sx={{ ...pillSx(hud, hasRange), gap: 0.75 }}>
        <Box component="b" sx={pillKeySx(hud)}>Placed</Box>
        <Tooltip title="Orders placed from">
          <Box
            component="input"
            type="date"
            value={from}
            max={to || undefined}
            onClick={openDatePicker}
            onFocus={openDatePicker}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              patchFilters({ from: e.target.value ? sastInstant(e.target.value) : undefined })}
            sx={dateInputSx(hud, mode)}
          />
        </Tooltip>
        <Box sx={{ color: hud.textMuted, fontSize: '0.8rem' }}>→</Box>
        <Tooltip title="Orders placed up to and including">
          <Box
            component="input"
            type="date"
            value={to}
            min={from || undefined}
            onClick={openDatePicker}
            onFocus={openDatePicker}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              patchFilters({ to: e.target.value ? exclusiveEnd(e.target.value) : undefined })}
            sx={dateInputSx(hud, mode)}
          />
        </Tooltip>
        {hasRange && (
          <Tooltip title="Clear date range">
            <IconButton
              size="small"
              onClick={() => patchFilters({ from: undefined, to: undefined })}
              sx={{ color: hud.textMuted }}
            >
              <ClearIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* Store filter lives in the app bar now — see StorePicker. */}
    </Box>
  );
}
